/**
 * **A section's buttons** — the `buttons` field kind (C20, ADR-0041 §1,
 * waves.md Wave 20 I-3; T-4.78).
 *
 * *"There should be no direct buttons in any section, we should able to
 * configure the action buttons"* (the owner, 2026-09-27). So a section draws
 * exactly the buttons the firm sets here — up to `max_items` (two), primary
 * then secondary — and none of its own.
 *
 * Each row is a label and a link of one of four kinds, as the vocabulary's
 * `link_kinds` lists them:
 *
 * - **A page** of the site — chosen from the draft's pages, stored by id so a
 *   renamed page keeps its button;
 * - **A booking flow** — a live flow, and a plan if the firm wants one chosen
 *   (`BookingLinkDialog`);
 * - **Client login** — the portal's sign-in, the header's own destination;
 * - **An address** — `https://`, `mailto:`, `tel:` or a path on the site.
 *
 * What is saved is the stored shape of I-3, `[{label, link}]`, and the service
 * is the only validator. **A half-made button is kept and named, never
 * dropped silently**: a label with no link, or a link with no label, saves and
 * shows a draft note here, and the site does not draw it until it is whole.
 */

import { Button, Field, Input, Select } from '@fiveox/ui'
import { CalendarCheck, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { BOOKING, useEvaluation } from '../../../lib/access'
import {
  site as siteApi,
  type ButtonLink,
  type ButtonLinkKind,
  type FieldSchema,
  type Page,
  type SectionButton,
} from '../../../lib/api/site'
import { BookingLinkDialog } from './BookingLinkPicker'

const ALL_KINDS: ButtonLinkKind[] = ['page', 'booking', 'login', 'external']

export const LINK_KIND_LABELS: Record<ButtonLinkKind, string> = {
  page: 'A page of your site',
  booking: 'A booking flow',
  login: 'Client login',
  external: 'An address',
}

/** The stored buttons, read defensively: a row that is not an object is dropped. */
export function readButtons(value: unknown): SectionButton[] {
  if (!Array.isArray(value)) return []
  return value
    .filter((b): b is Record<string, unknown> => Boolean(b) && typeof b === 'object')
    .map((b) => ({
      label: typeof b.label === 'string' ? b.label : '',
      link: b.link && typeof b.link === 'object' ? (b.link as ButtonLink) : null,
    }))
}

/** The link a kind starts from when the firm picks it — `null` until it is complete. */
export function startLink(kind: ButtonLinkKind | ''): ButtonLink | null {
  if (kind === 'login') return { kind: 'login' }
  if (kind === 'external') return { kind: 'external', href: '' }
  if (kind === 'page') return { kind: 'page', page_id: '' }
  return null
}

/**
 * What is missing from a button, in the words the builder shows under it —
 * `null` for a button that will be drawn. The site draws a button only when
 * it has both (ADR-0041 §1).
 */
export function buttonNote(button: SectionButton): string | null {
  const hasLabel = button.label.trim() !== ''
  const link = button.link
  const hasLink =
    link !== null &&
    (link.kind === 'login' ||
      (link.kind === 'page' && link.page_id !== '') ||
      (link.kind === 'booking' && link.flow_slug !== '') ||
      (link.kind === 'external' && link.href.trim() !== ''))
  if (hasLabel && hasLink) return null
  if (!hasLabel && !hasLink) return 'Draft — give it words and a link, or remove it.'
  if (!hasLabel) return 'Draft — this button has a link but no words, so it is not shown yet.'
  return 'Draft — this button has words but no link, so it is not shown yet.'
}

/** What `onChange` saves: the rows as they stand, an empty link as `null`. */
function toStored(rows: SectionButton[]): SectionButton[] {
  return rows.map((row) => {
    const link = row.link
    const empty =
      link === null ||
      (link.kind === 'page' && !link.page_id) ||
      (link.kind === 'external' && !link.href.trim()) ||
      (link.kind === 'booking' && !link.flow_slug)
    return { label: row.label, link: empty ? null : link }
  })
}

export function ButtonsField({
  field,
  value,
  error,
  onChange,
}: {
  field: FieldSchema
  value: unknown
  error: string | null
  onChange: (next: unknown) => void
}) {
  const max = field.max_items ?? 2
  const labelMax = field.label_max ?? 40
  const kinds = (field.link_kinds?.length ? field.link_kinds : ALL_KINDS).filter((k) => ALL_KINDS.includes(k))
  const stored = readButtons(value)
  // The rows as the firm is editing them: a kind chosen before its target is
  // held here, so choosing "A page" does not vanish because no page is picked.
  const [rows, setRows] = useState<SectionButton[]>(stored)
  useEffect(() => {
    // A new value from outside (undo, a reload) wins over the local draft.
    if (JSON.stringify(toStored(rows)) !== JSON.stringify(toStored(stored))) setRows(stored)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)])

  const commit = (next: SectionButton[]) => {
    setRows(next)
    onChange(toStored(next))
  }
  const setRow = (i: number, next: SectionButton) => commit(rows.map((r, j) => (j === i ? next : r)))

  return (
    <div data-buttons-field={field.name}>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">{field.label}</span>
        <span className="text-xs text-muted-foreground">
          {rows.length} of {max}
        </span>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        The first is the main button, the second a quieter one. A section shows only the buttons
        you set here.
      </p>
      {error && <p className="mb-1.5 text-xs text-destructive">{error}</p>}
      <div className="space-y-2">
        {rows.map((row, i) => (
          <ButtonRow
            key={i}
            index={i}
            row={row}
            kinds={kinds}
            labelMax={labelMax}
            onChange={(next) => setRow(i, next)}
            onRemove={() => commit(rows.filter((_, j) => j !== i))}
          />
        ))}
      </div>
      <Button
        variant="secondary"
        size="sm"
        className="mt-2"
        disabled={rows.length >= max}
        onClick={() => setRows([...rows, { label: '', link: null }])}
        data-buttons-add=""
      >
        <Plus size={14} /> Add a button
      </Button>
    </div>
  )
}

function ButtonRow({
  index,
  row,
  kinds,
  labelMax,
  onChange,
  onRemove,
}: {
  index: number
  row: SectionButton
  kinds: ButtonLinkKind[]
  labelMax: number
  onChange: (next: SectionButton) => void
  onRemove: () => void
}) {
  const { can } = useEvaluation()
  const [booking, setBooking] = useState(false)
  const [pendingKind, setPendingKind] = useState<ButtonLinkKind | ''>(row.link?.kind ?? '')
  const kind: ButtonLinkKind | '' = row.link?.kind ?? pendingKind
  const offered = kinds.filter((k) => k !== 'booking' || can(BOOKING, 'can_view') || kind === 'booking')
  const which = index === 0 ? 'Main button' : 'Second button'
  const note = buttonNote(row)

  const chooseKind = (next: ButtonLinkKind | '') => {
    setPendingKind(next)
    if (next === 'booking') {
      onChange({ ...row, link: row.link?.kind === 'booking' ? row.link : null })
      if (row.link?.kind !== 'booking') setBooking(true)
      return
    }
    onChange({ ...row, link: startLink(next) })
  }

  return (
    <div className="rounded-lg border border-border bg-surface-raised p-3" data-button-row={index}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground">{which}</span>
        <Button variant="ghost" size="sm" aria-label={`Remove the ${which.toLowerCase()}`} onClick={onRemove}>
          <Trash2 size={14} />
        </Button>
      </div>
      <div className="grid gap-2.5 sm:grid-cols-2">
        <Field label="Words on the button" hint={`Up to ${labelMax} characters.`}>
          {(control) => (
            <Input
              {...control}
              value={row.label}
              maxLength={labelMax}
              data-button-label={index}
              onChange={(e) => onChange({ ...row, label: e.target.value })}
            />
          )}
        </Field>
        <Field label="Goes to">
          {(control) => (
            <Select
              {...control}
              value={kind}
              data-button-kind={index}
              onChange={(e) => chooseKind(e.target.value as ButtonLinkKind | '')}
            >
              {/* C27 (UAT report #13): a button goes somewhere, so this is a
                  placeholder and not a choice — *Remove* takes a button away. */}
              <option value="" disabled hidden>
                Choose…
              </option>
              {offered.map((k) => (
                <option key={k} value={k}>
                  {LINK_KIND_LABELS[k]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      {kind === 'page' && (
        <PageChoice
          index={index}
          value={row.link?.kind === 'page' ? row.link.page_id : ''}
          onPick={(page_id) => onChange({ ...row, link: { kind: 'page', page_id } })}
        />
      )}
      {kind === 'external' && (
        <div className="mt-2.5">
          <Field label="Address" hint="https://…, mailto:…, tel:… or a path on your site such as /contact.">
            {(control) => (
              <Input
                {...control}
                inputMode="url"
                value={row.link?.kind === 'external' ? row.link.href : ''}
                data-button-href={index}
                onChange={(e) => onChange({ ...row, link: { kind: 'external', href: e.target.value } })}
              />
            )}
          </Field>
        </div>
      )}
      {kind === 'booking' && (
        <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1" data-booking-link="">
          <Button variant="ghost" size="sm" onClick={() => setBooking(true)} data-booking-link-open="">
            <CalendarCheck size={14} aria-hidden /> {row.link?.kind === 'booking' ? 'Change the flow' : 'Choose a flow'}
          </Button>
          {row.link?.kind === 'booking' && (
            <span className="text-xs text-muted-foreground" data-booking-link-current="">
              Books through “{row.link.flow_slug}”{row.link.plan_id ? ', with a plan chosen' : ''}.
            </span>
          )}
        </div>
      )}
      {kind === 'login' && (
        <p className="mt-2 text-xs text-muted-foreground">
          Opens your client portal’s sign-in — the same place as the Login in your header.
        </p>
      )}
      {note && (
        <p className="mt-2 text-xs text-muted-foreground" data-button-note={index}>
          {note}
        </p>
      )}

      {booking && (
        <BookingLinkDialog
          initial={
            row.link?.kind === 'booking' ? { slug: row.link.flow_slug, planId: row.link.plan_id ?? null } : null
          }
          onClose={() => setBooking(false)}
          onPick={(choice) => {
            onChange({ ...row, link: { kind: 'booking', ...choice } })
            setBooking(false)
          }}
        />
      )}
    </div>
  )
}

/** The draft's pages, fetched once when a button first needs them. */
function PageChoice({ index, value, onPick }: { index: number; value: string; onPick: (pageId: string) => void }) {
  const [pages, setPages] = useState<Page[] | null>(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let live = true
    siteApi
      .pages('draft')
      .then((res) => live && setPages(res.data))
      .catch(() => {
        if (!live) return
        setPages([])
        setFailed(true)
      })
    return () => {
      live = false
    }
  }, [])
  const known = pages?.some((p) => p.id === value) ?? false
  return (
    <div className="mt-2.5">
      <Field label="Page" hint={failed ? 'Your pages could not be loaded.' : undefined}>
        {(control) => (
          <Select
            {...control}
            value={value}
            data-button-page={index}
            disabled={pages === null}
            onChange={(e) => onPick(e.target.value)}
          >
            <option value="" disabled hidden>
              {pages === null ? 'Loading your pages…' : 'Choose a page…'}
            </option>
            {value && pages !== null && !known ? <option value={value}>A page not in this draft</option> : null}
            {(pages ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.title || p.slug} ({p.href})
              </option>
            ))}
          </Select>
        )}
      </Field>
    </div>
  )
}
